import { Component, EventEmitter, Input, Output } from '@angular/core';
import { LibraryPage, ReportConfig } from '@modules/report-generator/interfaces';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { AddPageDialogComponent } from '../../shared/add-page-dialog/add-page-dialog.component';
import { ReportGeneratorStore } from '@modules/report-generator/report-generator.store';

type SidebarMode = 'document' | 'library';

@Component({
  selector: 'app-report-sidebar',
  templateUrl: './report-sidebar.component.html',
  styleUrls: ['./report-sidebar.component.scss'],
})
export class ReportSidebarComponent {
  @Input() pageLibrary?: LibraryPage[] | null = [];
  @Input() pages: LibraryPage[] = [];
  @Input() selectedPageIndex: number = -1;
  @Input() reportConfig?: ReportConfig | null;
  @Output() pageSelected = new EventEmitter<number>();
  @Output() pageDeleted = new EventEmitter<number>();
  @Output() pageDropped = new EventEmitter<{ event: DragEvent; insertIndex: number }>();
  @Output() pageReordered = new EventEmitter<{ fromIndex: number; toIndex: number }>();

  private draggedPageIndex: number | null = null;

  currentMode: SidebarMode = 'document'; // Default to document view

  constructor(
    private readonly store: ReportGeneratorStore,
    private readonly dialog: MatDialog
  ) {}

  setMode(mode: SidebarMode): void {
    this.currentMode = mode;
  }

  onPageClick(index: number): void {
    this.pageSelected.emit(index);
  }

  deletePage(index: number): void {
    this.pageDeleted.emit(index);
  }

  getPageAspectRatio(page: LibraryPage): string {
    return page.orientation === 'landscape' ? '842/595' : '595/842';
  }

  onInsertPage(pageLibrary: LibraryPage[], reportConfig: ReportConfig, insertIndex: number): void {
    const dialogRef: MatDialogRef<AddPageDialogComponent> = this.dialog.open(AddPageDialogComponent, {
      width: '960px',
      disableClose: false,
      data: {
        pageLibrary: pageLibrary,
        reportConfig: reportConfig,
      },
    });

    dialogRef.afterClosed().subscribe((pageId: number | undefined) => {
      if (pageId) {
        const libraryPage = pageLibrary.find((p) => p.id === pageId);
        if (libraryPage) {
          this.store.insertPageAtIndex(libraryPage, insertIndex);
        }
      }
    });
  }

  onPageDragStart(event: DragEvent, fromIndex: number): void {
    this.draggedPageIndex = fromIndex;
    event.dataTransfer!.effectAllowed = 'move';
    event.dataTransfer!.setData('text/plain', fromIndex.toString());
    (event.target as HTMLElement).closest('.document-page-item')?.classList.add('dragging');
  }

  onPageDragOver(event: DragEvent): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';

    // Only apply drag-over styling for page reordering (text/plain), not library drops (application/json)
    const types = event.dataTransfer?.types || [];
    if (types.includes('text/plain')) {
      const pageItem = (event.target as HTMLElement).closest('.document-page-item');
      if (pageItem) {
        pageItem.classList.add('drag-over');
      }
    }
  }

  onPageDrop(event: DragEvent, toIndex: number): void {
    event.preventDefault();
    const pageItem = (event.target as HTMLElement).closest('.document-page-item');
    if (pageItem) {
      pageItem.classList.remove('drag-over');
    }

    if (this.draggedPageIndex !== null && this.draggedPageIndex !== toIndex) {
      this.pageReordered.emit({ fromIndex: this.draggedPageIndex, toIndex });
    }

    this.draggedPageIndex = null;
  }

  onPageDragLeave(event: DragEvent): void {
    const pageItem = (event.target as HTMLElement).closest('.document-page-item');
    if (pageItem) {
      pageItem.classList.remove('drag-over');
    }
  }
}
